import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Checkbox,
  TextField,
  CircularProgress,
  Divider,
  Breadcrumbs,
  Link,
  Chip,
  Paper,
  Slide,
  Fab,
  IconButton,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ChevronRight,
  CheckCircle,
  ArrowLeft as ArrowBack,
  ShoppingCart,
  Person,
  FilterList,
  ViewList,
  ViewModule,
  LabelImportant,
  FitnessCenter,
  Settings,
  FolderSpecial,
  MenuBook,
  LocalLibrary,
} from '@mui/icons-material';
import { Copy } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { LoadingButton } from '@mui/lab';
import { get_notes_history, import_notes_history } from 'src/auth/context/jwt/library';

// Type indicator chips
const TypeChip = ({ type }) => {
  if (type === 'WORKOUT') {
    return (
      <Chip
        size="small"
        icon={<FitnessCenter fontSize="small" />}
        label="Workout"
        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', '& .MuiChip-icon': { color: '#2e7d32' } }}
      />
    );
  }
  if (type === 'SET') {
    return (
      <Chip
        size="small"
        icon={<Settings fontSize="small" />}
        label="Set"
        sx={{ bgcolor: '#fff3e0', color: '#ed6c02', '& .MuiChip-icon': { color: '#ed6c02' } }}
      />
    );
  }
  return (
    <Chip
      size="small"
      icon={<LabelImportant fontSize="small" />}
      label="Exercise"
      sx={{ bgcolor: '#fce4ec', color: '#c2185b', '& .MuiChip-icon': { color: '#c2185b' } }}
    />
  );
};

// Source indicator chip
const SourceChip = ({ category }) => {
  const getSourceProps = () => {
    switch (category) {
      case 'train_program':
        return {
          icon: <Person fontSize="small" />,
          label: 'Train Program',
          sx: { bgcolor: '#e3f2fd', color: '#1976d2' },
        };
      case 'library_program':
        return {
          icon: <MenuBook fontSize="small" />,
          label: 'Library Program',
          sx: { bgcolor: '#f3e5f5', color: '#7b1fa2' },
        };
      case 'library_individual':
        return {
          icon: <LocalLibrary fontSize="small" />,
          label: 'Library Individual',
          sx: { bgcolor: '#fff9c4', color: '#f57f17' },
        };
      default:
        return {
          icon: <FolderSpecial fontSize="small" />,
          label: 'Other',
          sx: { bgcolor: '#e0e0e0', color: '#616161' },
        };
    }
  };

  const props = getSourceProps();
  return <Chip size="small" icon={props.icon} label={props.label} sx={props.sx} />;
};

// Search Bar Component
function SearchBar({ onSearch, onFilter, filterType }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = (filterTypeValue) => {
    if (filterTypeValue) onFilter(filterTypeValue);
    setFilterAnchorEl(null);
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
      <TextField
        fullWidth
        placeholder="Search by name, program, path..."
        value={searchTerm}
        onChange={handleSearch}
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover fieldset': { borderColor: '#667eea' },
            '&.Mui-focused fieldset': { borderColor: '#667eea' },
          },
        }}
      />
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={handleFilterClick}
          startIcon={<FilterList />}
          size="small"
          sx={{
            borderColor: '#667eea',
            color: '#667eea',
            whiteSpace: 'nowrap',
            minWidth: 'auto',
          }}
        >
          Filter
          {filterType !== 'all' && (
            <Chip
              label={filterType.replace(/_/g, ' ')}
              size="small"
              sx={{
                ml: 1,
                bgcolor: '#667eea',
                color: 'white',
                height: 20,
                '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
                textTransform: 'capitalize',
              }}
            />
          )}
        </Button>
      </Stack>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => handleFilterClose()}
      >
        <MenuItem onClick={() => handleFilterClose('all')}>
          <ListItemIcon>
            <FolderSpecial fontSize="small" />
          </ListItemIcon>
          <ListItemText>All Items</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleFilterClose('train_program_workouts')}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>Train Program - Workouts</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleFilterClose('train_program_sets')}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>Train Program - Sets</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleFilterClose('library_program_workouts')}>
          <ListItemIcon>
            <MenuBook fontSize="small" />
          </ListItemIcon>
          <ListItemText>Library Program - Workouts</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleFilterClose('library_program_sets')}>
          <ListItemIcon>
            <MenuBook fontSize="small" />
          </ListItemIcon>
          <ListItemText>Library Program - Sets</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleFilterClose('library_individual_exercise_workouts')}>
          <ListItemIcon>
            <LocalLibrary fontSize="small" />
          </ListItemIcon>
          <ListItemText>Library Individual - Workouts</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleFilterClose('library_individual_exercise_sets')}>
          <ListItemIcon>
            <LocalLibrary fontSize="small" />
          </ListItemIcon>
          <ListItemText>Library Individual - Sets</ListItemText>
        </MenuItem>
      </Menu>
    </Stack>
  );
}

// Exercise Card Component
function ExerciseCard({ exercise, isSelected, onToggle, viewMode }) {
  const getIconByType = () => {
    if (exercise.type === 'WORKOUT') {
      return <FitnessCenter fontSize="small" sx={{ color: '#2e7d32' }} />;
    }
    if (exercise.type === 'SET') {
      return <Settings fontSize="small" sx={{ color: '#ed6c02' }} />;
    }
    return <LabelImportant fontSize="small" sx={{ color: '#667eea' }} />;
  };

  if (viewMode === 'grid') {
    return (
      <Card
        elevation={0}
        sx={{
          border: isSelected ? '2px solid #667eea' : '1px solid rgba(0,0,0,0.08)',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          },
        }}
        onClick={() => onToggle(exercise.id)}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, flexGrow: 1 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Checkbox
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggle(exercise.id);
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' }, p: 0 }}
              />
              <TypeChip type={exercise.type} />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              {getIconByType()}
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, fontSize: '0.9rem', wordBreak: 'break-word' }}
              >
                {exercise.name}
              </Typography>
            </Stack>

            <SourceChip category={exercise.category} />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                fontSize: '0.7rem',
                wordBreak: 'break-word',
              }}
            >
              {exercise.path}
            </Typography>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <Chip
                label={`${exercise.notes_count} notes`}
                size="small"
                sx={{ fontSize: '0.7rem' }}
                color="primary"
                variant="filled"
              />
              <Typography variant="caption" color="text.secondary">
                {new Date(exercise.created_at).toLocaleDateString()}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // List view
  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        border: isSelected ? '2px solid #667eea' : '1px solid rgba(0,0,0,0.08)',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateX(4px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          borderColor: '#667eea',
        },
      }}
      onClick={() => onToggle(exercise.id)}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
          spacing={2}
        >
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggle(exercise.id);
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }}
          />
          <Box sx={{ flexGrow: 1, width: '100%' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                {getIconByType()}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                  {exercise.name}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                <TypeChip type={exercise.type} />
                <SourceChip category={exercise.category} />
              </Stack>
            </Stack>

            <Box sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic', wordBreak: 'break-word' }}
              >
                {exercise.path}
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              flexWrap="wrap"
              gap={1}
            >
              <Chip
                label={`${exercise.notes_count} ${exercise.notes_count === 1 ? 'note' : 'notes'}`}
                size="small"
                sx={{ fontWeight: 500 }}
                color="primary"
                variant="filled"
              />
              <Typography variant="caption" color="text.secondary">
                {new Date(exercise.created_at).toLocaleDateString()}
              </Typography>
              {exercise.programName && (
                <Chip
                  label={exercise.programName}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#667eea', color: '#667eea' }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function NotesHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSet, currentWorkout, currentProgram, entity_id, entity_type } =
    location.state || {};

  const [selectedExercises, setSelectedExercises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showBottomBar, setShowBottomBar] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [filterType, setFilterType] = useState('all');
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);

  const isWorkout = entity_type.includes('workouts');
  const isSet = entity_type.includes('sets');

  // Fetch data from API
  const fetchWorkouts = async () => {
    try {
      setLoading(true);
      const response = await get_notes_history();
      setEntries(response.data.entries);
      setFilteredEntries(response.data.entries);
    } catch (err) {
      toast.error('Failed to fetch workouts. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkouts();
  }, []);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...entries];

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(
        (entry) =>
          entry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (entry.programName &&
            entry.programName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (entry.exerciseName &&
            entry.exerciseName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply category filter
    if (filterType !== 'all') {
      switch (filterType) {
        case 'train_program_workouts':
          filtered = filtered.filter(
            (entry) => entry.category === 'train_program' && entry.type === 'WORKOUT'
          );
          break;
        case 'train_program_sets':
          filtered = filtered.filter(
            (entry) => entry.category === 'train_program' && entry.type === 'SET'
          );
          break;
        case 'library_program_workouts':
          filtered = filtered.filter(
            (entry) => entry.category === 'library_program' && entry.type === 'WORKOUT'
          );
          break;
        case 'library_program_sets':
          filtered = filtered.filter(
            (entry) => entry.category === 'library_program' && entry.type === 'SET'
          );
          break;
        case 'library_individual_exercise_workouts':
          filtered = filtered.filter(
            (entry) => entry.category === 'library_individual' && entry.type === 'WORKOUT'
          );
          break;
        case 'library_individual_exercise_sets':
          filtered = filtered.filter(
            (entry) => entry.category === 'library_individual' && entry.type === 'SET'
          );
          break;
        default:
          break;
      }
    }

    setFilteredEntries(filtered);
  }, [searchTerm, filterType, entries]);

  const handleToggleExercise = (exerciseId) => {
    setSelectedExercises((prev) =>
      prev.includes(exerciseId) ? prev.filter((id) => id !== exerciseId) : [...prev, exerciseId]
    );
    setShowBottomBar(true);
  };

  const handleImportNotes = async () => {
    if (selectedExercises.length === 0) {
      toast.error('Please select at least one exercise to copy notes from');
      return;
    }

    setImporting(true);
    try {
      const data = {
        entity_id,
        type: entity_type,
        selected_ids: selectedExercises,
      };
      const response = await import_notes_history(data);
      // Show success message
      toast.success(response?.message);

      // Navigate back to the workout discussion page
      navigate(-1);
    } catch (error) {
      toast.error('Failed to copy notes. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const getTotalNotesCount = () =>
    selectedExercises.reduce((total, id) => {
      const exercise = filteredEntries.find((e) => e.id === id);
      return total + (exercise?.notes_count || 0);
    }, 0);

  const handleSelectAll = () => {
    const allExerciseIds = filteredEntries.map((ex) => ex.id);
    if (selectedExercises.length === allExerciseIds.length) {
      setSelectedExercises([]);
    } else {
      setSelectedExercises(allExerciseIds);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: '#667eea' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 2, md: 4 }, pb: { xs: 12, md: 16 } }}
    >
      <Stack spacing={3}>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<ChevronRight fontSize="small" />} aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <ArrowBack fontSize="small" />
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Back {isWorkout ? 'to workouts' : isSet ? 'to sets' : ''}
            </Typography>
            <Typography variant="body2" sx={{ display: { xs: 'block', sm: 'none' } }}>
              Back
            </Typography>
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Notes History
          </Typography>
        </Breadcrumbs>

        {/* Title Section */}
        <Card
          elevation={0}
          sx={{
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea15, #764ba215)',
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="overline" sx={{ color: '#667eea', fontWeight: 600 }}>
                  Copy Notes To {isSet ? 'Set' : isWorkout && 'Workout'}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    wordBreak: 'break-word',
                  }}
                >
                  {isSet && currentSet}
                  {isWorkout && currentWorkout}
                </Typography>
                {isSet && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontStyle="italic"
                    fontWeight="bold"
                  >
                    Workout: {currentWorkout}
                  </Typography>
                )}
                {currentProgram && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontStyle="italic"
                    fontWeight="bold"
                  >
                    Program: {currentProgram}
                  </Typography>
                )}
              </Box>
              <Badge
                badgeContent={filteredEntries?.length}
                color="primary"
                sx={{ '& .MuiBadge-badge': { bgcolor: '#667eea' } }}
              >
                <Chip label="Available Items" variant="outlined" />
              </Badge>
            </Stack>
          </CardContent>
        </Card>

        {/* Main Content */}
        <Paper
          elevation={0}
          sx={{ borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden' }}
        >
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Select Items to Copy Notes From
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title="List View">
                  <IconButton
                    onClick={() => setViewMode('list')}
                    color={viewMode === 'list' ? 'primary' : 'default'}
                    size="small"
                  >
                    <ViewList />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Grid View">
                  <IconButton
                    onClick={() => setViewMode('grid')}
                    color={viewMode === 'grid' ? 'primary' : 'default'}
                    size="small"
                  >
                    <ViewModule />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <SearchBar onSearch={setSearchTerm} onFilter={setFilterType} filterType={filterType} />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAll}
                sx={{ borderColor: '#667eea', color: '#667eea' }}
              >
                {selectedExercises.length === filteredEntries.length && filteredEntries.length > 0
                  ? 'Deselect All'
                  : 'Select All'}
              </Button>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredEntries.length} items
              </Typography>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {viewMode === 'grid' ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(320px, 1fr))' },
                  gap: 2,
                }}
              >
                {filteredEntries.map((entry) => (
                  <ExerciseCard
                    key={entry.id}
                    exercise={entry}
                    isSelected={selectedExercises.includes(entry.id)}
                    onToggle={handleToggleExercise}
                    viewMode={viewMode}
                  />
                ))}
              </Box>
            ) : (
              filteredEntries.map((entry) => (
                <ExerciseCard
                  key={entry.id}
                  exercise={entry}
                  isSelected={selectedExercises.includes(entry.id)}
                  onToggle={handleToggleExercise}
                  viewMode={viewMode}
                />
              ))
            )}
            {filteredEntries.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  No items found matching your criteria.
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Stack>

      {/* Floating Action Button */}
      {selectedExercises.length > 0 && (
        <Fab
          variant="extended"
          onClick={() =>
            document.getElementById('selection-summary')?.scrollIntoView({ behavior: 'smooth' })
          }
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
            bgcolor: '#667eea',
            color: 'white',
            '&:hover': { bgcolor: '#5568d3' },
            gap: 1,
            zIndex: 1000,
          }}
        >
          <ShoppingCart fontSize="small" />
          {selectedExercises.length} Selected
        </Fab>
      )}

      {/* Sticky Bottom Selection Summary */}
      <Slide
        direction="up"
        in={selectedExercises.length > 0 && showBottomBar}
        mountOnEnter
        unmountOnExit
      >
        <Paper
          id="selection-summary"
          elevation={10}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            bgcolor: 'background.paper',
            borderTop: '3px solid #667eea',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.12)',
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2, md: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={{ xs: 2, sm: 3 }}
              sx={{ py: { xs: 2, sm: 3 } }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    bgcolor: '#667eea',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircle fontSize="large" sx={{ color: 'white' }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    {selectedExercises.length} Item{selectedExercises.length !== 1 ? 's' : ''}{' '}
                    Selected
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Notes:
                    </Typography>
                    <Chip
                      label={getTotalNotesCount()}
                      size="small"
                      sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 600 }}
                    />
                  </Stack>
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setSelectedExercises([])}
                  sx={{ borderColor: '#667eea', color: '#667eea', px: 3 }}
                >
                  Clear All
                </Button>
                <LoadingButton
                  loading={importing}
                  onClick={handleImportNotes}
                  variant="contained"
                  startIcon={<Copy fontSize="small" />}
                  sx={{
                    bgcolor: '#667eea',
                    '&:hover': { bgcolor: '#5568d3' },
                    borderRadius: 2,
                    px: 4,
                    minWidth: 160,
                  }}
                >
                  Copy Notes
                </LoadingButton>
              </Stack>
            </Stack>
          </Container>
        </Paper>
      </Slide>
    </Container>
  );
}
